/**
 * Email Entry Step
 * Step 1: Enter email address and detect provider via MX records
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "use-debounce";
import { Input } from "@/components/base/input/input";
import { Button } from "@/components/base/buttons/button";
import { Mail01, ArrowRight, CheckCircle, Loading03 } from "@untitledui/icons";
import { EmailProvider } from "@/types/api/email";
import { getProviderDisplayName } from "@/data/api/email";
import { useDetectEmailProvider } from "@/hooks/api/use-email-accounts";

interface EmailEntryStepProps {
    initialEmail?: string;
    initialDisplayName?: string;
    onSubmit: (email: string, displayName: string, provider: EmailProvider) => void;
    onCancel: () => void;
}

export const EmailEntryStep = ({
    initialEmail = "",
    initialDisplayName = "",
    onSubmit,
    onCancel,
}: EmailEntryStepProps) => {
    const { t } = useTranslation(["pages:email-settings"]);

    const [email, setEmail] = useState(initialEmail);
    const [displayName, setDisplayName] = useState(initialDisplayName);
    const [emailError, setEmailError] = useState<string | null>(null);

    // Debounce email for API call (500ms delay)
    const [debouncedEmail] = useDebounce(email, 500);

    // Detect provider via MX records API
    const { data: providerResult, isLoading: isDetecting } = useDetectEmailProvider(
        debouncedEmail,
        !!debouncedEmail && validateEmail(debouncedEmail)
    );

    // Map API result to EmailProvider enum
    const detectedProvider: EmailProvider | null = (() => {
        if (!providerResult?.detected_provider) {
            // If API returns null but it's a valid email, fall back to IMAP/SMTP
            if (providerResult && debouncedEmail && validateEmail(debouncedEmail)) {
                return EmailProvider.IMAP_SMTP;
            }
            return null;
        }
        if (providerResult.detected_provider === "gmail") return EmailProvider.GMAIL;
        if (providerResult.detected_provider === "outlook") return EmailProvider.OUTLOOK;
        return EmailProvider.IMAP_SMTP;
    })();

    // Get display name from API result or fallback
    const providerDisplayName = providerResult?.provider_name ||
        (detectedProvider ? getProviderDisplayName(detectedProvider) : null);

    function validateEmail(value: string): boolean {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value);
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Validate email
        if (!email) {
            setEmailError(t("emailEntry.emailRequired"));
            return;
        }

        if (!validateEmail(email)) {
            setEmailError(t("emailEntry.emailInvalid"));
            return;
        }

        if (!detectedProvider) {
            setEmailError(t("emailEntry.emailInvalid"));
            return;
        }

        setEmailError(null);
        onSubmit(email, displayName, detectedProvider);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
                <h3 className="text-base font-medium text-primary">
                    {t("emailEntry.title")}
                </h3>
                <p className="mt-1 text-sm text-tertiary">
                    {t("emailEntry.description")}
                </p>
            </div>

            {/* Email Field */}
            <div className="space-y-2">
                <Input
                    type="email"
                    label={`${t("emailEntry.emailLabel")} *`}
                    value={email}
                    onChange={(value) => {
                        setEmail(value);
                        setEmailError(null);
                    }}
                    placeholder={t("emailEntry.emailPlaceholder")}
                    icon={Mail01}
                    isInvalid={!!emailError}
                    hint={emailError || undefined}
                />
                {/* Provider Detection Status */}
                {isDetecting && email && validateEmail(email) && (
                    <div className="flex items-center gap-2 text-sm text-tertiary">
                        <Loading03 className="h-4 w-4 animate-spin" />
                        {t("emailEntry.detectingProvider", { defaultValue: "Detecting provider..." })}
                    </div>
                )}
                {detectedProvider && !emailError && !isDetecting && (
                    <div className="flex items-center gap-2 text-sm text-fg-success-secondary">
                        <CheckCircle className="h-4 w-4" />
                        {t("emailEntry.providerDetected", {
                            provider: providerDisplayName,
                        })}
                    </div>
                )}
            </div>

            {/* Display Name Field */}
            <Input
                type="text"
                label={t("emailEntry.displayNameLabel")}
                value={displayName}
                onChange={(value) => setDisplayName(value)}
                placeholder={t("emailEntry.displayNamePlaceholder")}
                hint={t("emailEntry.displayNameHint")}
            />

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
                <Button
                    type="button"
                    onClick={onCancel}
                    color="secondary"
                    size="sm"
                >
                    {t("actions.cancel")}
                </Button>
                <Button
                    type="submit"
                    isDisabled={!email || !detectedProvider || isDetecting}
                    color="primary"
                    size="sm"
                    iconTrailing={ArrowRight}
                >
                    {t("actions.continue")}
                </Button>
            </div>
        </form>
    );
};
